/**
 * @file orders.ts
 *
 * Order API for the shop admin. Reads ({@link fetchOrders}, {@link fetchOrderById})
 * are wired to the real commerce-engine `/v1` orders API and map the `/v1` shapes to
 * the design-reviewed {@link ShopOrder} (a lighter list summary vs the full detail,
 * like the products API). Writes are the real order actions {@link cancelOrder},
 * {@link refundOrder}, {@link markOrderPaid} (each returning the updated order),
 * {@link fulfilOrder} (returns the shipment, so callers re-fetch), {@link deleteOrder}
 * (a state-gated soft delete), and {@link overrideOrderStatus} (the admin status-set
 * backdoor). This layer is the only place that knows the backend's specifics, so the
 * store and screens stay untouched.
 */

import { v1Get, v1Mutate } from './v1-request';
import { minorUnitsToDecimal } from './money';
import type { WcResult } from './types';
import type {
  FetchOrdersParams,
  ShopOrder,
  ShopOrderAddress,
  ShopOrderLineItem,
} from './shop-types';
import type {
  CancelOrderRequestV1,
  OrderAddressV1,
  OrderLineItemV1,
  OrderResponseV1,
  OrderStatusV1,
  OrderSummaryResponseV1,
  OverrideOrderStatusRequestV1,
  RefundOrderRequestV1,
} from './v1-types';

/** Default orders per page for the list view. */
export const ORDERS_PER_PAGE = 20;

/** `/v1` order status → the WooCommerce-style status the screens use (only `on_hold` differs). */
const V1_TO_SHOP_STATUS: Record<OrderStatusV1, string> = {
  pending: 'pending',
  on_hold: 'on-hold',
  processing: 'processing',
  completed: 'completed',
  cancelled: 'cancelled',
  refunded: 'refunded',
  failed: 'failed',
};

/** WooCommerce-style status (filter) → the `/v1` order status. */
const SHOP_TO_V1_STATUS: Record<string, OrderStatusV1> = {
  pending: 'pending',
  'on-hold': 'on_hold',
  processing: 'processing',
  completed: 'completed',
  cancelled: 'cancelled',
  refunded: 'refunded',
  failed: 'failed',
};

/** An empty address, used when an order has no billing/shipping snapshot of a given type. */
const EMPTY_ADDRESS: ShopOrderAddress = {
  first_name: '',
  last_name: '',
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  postcode: '',
  country: '',
};

/** Maps a `/v1` order address to the snake_case {@link ShopOrderAddress}; empty when absent. */
function toShopAddress(
  address: OrderAddressV1 | undefined,
  fallbackEmail?: string | null,
): ShopOrderAddress {
  if (!address) {
    return EMPTY_ADDRESS;
  }
  return {
    first_name: address.firstName ?? '',
    last_name: address.lastName ?? '',
    email: address.email ?? fallbackEmail ?? undefined,
    phone: address.phone ?? undefined,
    address_1: address.line1 ?? '',
    address_2: address.line2 ?? '',
    city: address.city ?? '',
    state: address.region ?? '',
    postcode: address.postcode ?? '',
    country: address.country ?? '',
  };
}

/** Maps a `/v1` order line item to the {@link ShopOrderLineItem} the drawer renders. */
function toShopLineItem(item: OrderLineItemV1): ShopOrderLineItem {
  return {
    id: item.id,
    name: item.name,
    quantity: item.quantity,
    total: minorUnitsToDecimal(item.totalAmount),
    price: Number(minorUnitsToDecimal(item.unitPriceAmount)),
    sku: item.sku ?? '',
    image: item.imageUrl,
    // Mirrors the engine's shippable predicate (product lines, excluding digital).
    shippable: item.kind === 'product' && item.itemType !== 'digital',
  };
}

/**
 * Maps a full `/v1` order to the {@link ShopOrder} the details drawer consumes:
 * minor-unit totals as decimal strings, the billing/shipping address split, the
 * line items, and the discounts as coupon lines. `currency_symbol` is left empty
 * (not on `/v1`); the screens fall back to the shop settings symbol. The payment
 * method degrades to the ledger's display-only payment type (`""` when none).
 *
 * @param o - The `/v1` order resource (admin audience).
 *
 * @returns The order in the design-reviewed `Shop*` shape.
 */
export function toShopOrder(o: OrderResponseV1): ShopOrder {
  const billing = o.addresses.find((a) => a.type === 'billing');
  const shipping = o.addresses.find((a) => a.type === 'shipping');
  return {
    id: o.id,
    number: String(o.number ?? ''),
    status: V1_TO_SHOP_STATUS[o.status] ?? o.status,
    payment_status: o.paymentStatus,
    fulfilment_status: o.fulfilmentStatus,
    payment_type: o.payments[0]?.paymentType ?? o.paymentMethod ?? '',
    currency: o.currency,
    currency_symbol: '',
    total: minorUnitsToDecimal(o.totals.grandTotal),
    total_tax: minorUnitsToDecimal(o.totals.taxTotal),
    discount_total: minorUnitsToDecimal(o.totals.discountTotal),
    shipping_total: minorUnitsToDecimal(o.totals.shippingTotal),
    date_created: o.createdAt,
    // Prefer the method resolved from a real payment event; otherwise fall back to the
    // sub-method / method selected at placement so the drawer still shows how an unpaid
    // (or manual / pre-webhook) order is being paid.
    payment_method_title:
      o.payments[0]?.paymentMethodTitle ??
      o.payments[0]?.paymentType ??
      o.paymentSubType ??
      o.paymentMethod ??
      '',
    customer_note: o.customer.note ?? '',
    billing: toShopAddress(billing, o.customer.email),
    shipping: toShopAddress(shipping),
    line_items: o.lineItems.map(toShopLineItem),
    coupon_lines: o.discounts.map((d) => ({
      code: d.code ?? '',
      discount: minorUnitsToDecimal(d.amount),
    })),
  };
}

/**
 * Maps a `/v1` order summary (list row) to a {@link ShopOrder}. The summary is
 * lighter than the full order, so the fields it omits (line items, addresses, the
 * itemised totals, coupons) default to empty. The billing display name, email, and the
 * payment-method title are carried so the list "Customer" and "Payment method" columns
 * resolve (the customer falls back to email, then number). The drawer fetches the full
 * order by id when the rest is needed.
 *
 * @param s - The `/v1` order summary (admin audience).
 *
 * @returns The order in the design-reviewed `Shop*` shape (list fields only).
 */
export function toShopOrderSummary(s: OrderSummaryResponseV1): ShopOrder {
  return {
    id: s.id,
    number: String(s.number ?? ''),
    status: V1_TO_SHOP_STATUS[s.status] ?? s.status,
    payment_status: s.paymentStatus,
    // The manual-workflow actions (Mark as paid, Fulfil) live in the drawer, which
    // re-fetches the full order; the lighter list summary carries neither, so both default empty.
    fulfilment_status: '',
    payment_type: '',
    currency: s.currency,
    currency_symbol: '',
    total: minorUnitsToDecimal(s.grandTotal),
    total_tax: '',
    discount_total: '',
    shipping_total: '',
    date_created: s.createdAt,
    payment_method_title: s.paymentMethodTitle ?? '',
    customer_note: '',
    // The summary's name is already composed (not split), and the list reads it via
    // customerName(order); put the whole display name in first_name for that row.
    billing: {
      ...EMPTY_ADDRESS,
      first_name: s.customerName ?? '',
      email: s.customerEmail ?? undefined,
    },
    shipping: EMPTY_ADDRESS,
    line_items: [],
    coupon_lines: [],
  };
}

/**
 * Fetches a page of orders with an optional status filter and search.
 *
 * The Woo status filter is multi-select (comma-joined); `/v1` `status` accepts the
 * full set (matching any), so every selected status is mapped to its `/v1` value and
 * sent comma-separated (mirroring the products category filter). The free-text search
 * maps to the `/v1` `q` param. Both are omitted when empty so the query carries only
 * active filters.
 *
 * @param params - Pagination, status, and search options.
 *
 * @returns The orders page wrapped in a {@link WcResult} (with totals).
 *
 * @throws {WcApiError} When the request fails.
 */
export async function fetchOrders(
  params: FetchOrdersParams = {},
): Promise<WcResult<ShopOrder[]>> {
  const { page = 1, perPage = ORDERS_PER_PAGE, status = '', search = '' } = params;
  const qs = new URLSearchParams({ page: String(page), pageSize: String(perPage) });
  // Map every selected Woo status to its `/v1` value (dropping any unknown), then send the
  // whole set comma-separated; `/v1` matches an order in ANY of them.
  const v1Statuses = status
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => SHOP_TO_V1_STATUS[s])
    .filter(Boolean);
  if (v1Statuses.length > 0) {
    qs.set('status', v1Statuses.join(','));
  }
  if (search) {
    qs.set('q', search);
  }
  const res = await v1Get<OrderSummaryResponseV1[]>(`/orders?${qs.toString()}`);
  return {
    data: res.data.map(toShopOrderSummary),
    total: res.total,
    totalPages: res.totalPages,
  };
}

/**
 * Fetches a single order by id to populate the details drawer (the list rows carry
 * only the lighter summary, so the drawer always fetches the full order).
 *
 * @param id - The order id to fetch.
 *
 * @returns The order.
 *
 * @throws {WcApiError} When the request fails (e.g. 404 for an unknown id).
 */
export async function fetchOrderById(id: string): Promise<ShopOrder> {
  const res = await v1Get<OrderResponseV1>(`/orders/${id}`);
  return toShopOrder(res.data);
}

/**
 * Cancels an order (`POST /v1/orders/:id/cancel`). The engine voids any outstanding
 * authorization and restocks inventory where applicable, then returns the updated
 * order.
 *
 * @param id - The order id to cancel.
 * @param reason - Optional human-readable cancellation reason.
 *
 * @returns The updated order, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails (e.g. an `order_invalid_transition` 409).
 */
export async function cancelOrder(id: string, reason?: string): Promise<ShopOrder> {
  // Always refund captured funds on an admin cancel — the engine ignores the flag when
  // nothing was captured, and a cancelled order must never silently keep the money.
  const body: CancelOrderRequestV1 = reason ? { reason, refund: true } : { refund: true };
  const res = await v1Mutate<OrderResponseV1>(`/orders/${id}/cancel`, 'POST', body);
  return toShopOrder(res);
}

/**
 * Fully refunds an order (`POST /v1/orders/:id/refunds` with `fullAmount: true`). The
 * admin issues full refunds only; the partial amount / line-item breakdown is not sent.
 *
 * @param id - The order id to refund.
 * @param opts - Optional refund reason and whether to restock the refunded items.
 *
 * @returns The updated order, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails (e.g. an `order_refund_in_progress` 409).
 */
export async function refundOrder(
  id: string,
  opts: { reason?: string; restock?: boolean } = {},
): Promise<ShopOrder> {
  const body: RefundOrderRequestV1 = {
    fullAmount: true,
    ...(opts.reason ? { reason: opts.reason } : {}),
    ...(opts.restock ? { restock: opts.restock } : {}),
  };
  const res = await v1Mutate<OrderResponseV1>(`/orders/${id}/refunds`, 'POST', body);
  return toShopOrder(res);
}

/**
 * Soft-deletes an order (`DELETE /v1/orders/:id`, 204 No Content). The engine permits
 * this only for `cancelled` or `completed` orders and rejects anything else with
 * `order_not_deletable` (409); the caller surfaces that as an error toast.
 *
 * @param id - The order id to delete.
 *
 * @throws {WcApiError} When the request fails (e.g. an `order_not_deletable` 409).
 */
export async function deleteOrder(id: string): Promise<void> {
  await v1Mutate<void>(`/orders/${id}`, 'DELETE');
}

/**
 * Records a manual / offline payment against an order (`POST /v1/orders/:id/mark-paid`).
 * No amount is sent, so the engine settles the full outstanding balance. Surfaced only for
 * `manual`-payment orders that are still unpaid; the engine rejects an already-paid order
 * with `order_already_paid` (409).
 *
 * @param id - The order id to mark paid.
 *
 * @returns The updated order, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails (e.g. an `order_already_paid` 409).
 */
export async function markOrderPaid(id: string): Promise<ShopOrder> {
  const res = await v1Mutate<OrderResponseV1>(`/orders/${id}/mark-paid`, 'POST', {});
  return toShopOrder(res);
}

/**
 * Fulfils an order in full (`POST /v1/orders/:orderId/shipments`): creates one shipment
 * covering every line at its ordered quantity, which re-projects the order's fulfilment
 * status. No carrier/tracking is sent (partial fulfilment and tracking are out of scope).
 * The response is the shipment, not the order, so callers re-fetch the order afterwards.
 *
 * @param id - The order id to fulfil.
 * @param items - The lines to ship: `{ orderItemId, quantity }` for every order line.
 *
 * @throws {WcApiError} When the request fails (e.g. a `shipment_qty_exceeded` 422).
 */
export async function fulfilOrder(
  id: string,
  items: { orderItemId: string; quantity: number }[],
): Promise<void> {
  await v1Mutate(`/orders/${id}/shipments`, 'POST', { items });
}

/**
 * Audit reason sent when the admin leaves the status-change reason blank. The engine
 * requires a non-empty reason (it lands on the `manual_status_override` audit event,
 * not in the UI), so this is a fixed English string rather than a localized message.
 */
const DEFAULT_OVERRIDE_REASON = 'Manual status change from admin';

/**
 * Force-sets an order's status (`POST /v1/orders/:id/status`, the engine's temporary
 * admin status-override backdoor). Any status can be set from any status; the engine
 * runs the target status's side effects (restock, coupon reversal, grants, emails) but
 * never moves money — refunds/cancellations that must move money go through
 * {@link refundOrder} / {@link cancelOrder} instead. Both engine flags are hardcoded
 * on: the customer is notified and side effects run. When the override kill switch
 * (the engine's `orderAdmin.statusOverrideEnabled` config) is off the engine
 * answers a generic 404.
 *
 * @param id - The order id.
 * @param status - The target status, in the WooCommerce-style shop vocabulary
 *   (e.g. `'on-hold'`); mapped to its `/v1` value before sending.
 * @param reason - Optional audit reason; a canned default fills a blank one.
 *
 * @returns The updated order, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails (e.g. a 404 while the override is disabled).
 */
export async function overrideOrderStatus(
  id: string,
  status: string,
  reason?: string,
): Promise<ShopOrder> {
  const body: OverrideOrderStatusRequestV1 = {
    status: SHOP_TO_V1_STATUS[status],
    reason: reason?.trim() || DEFAULT_OVERRIDE_REASON,
    notifyCustomer: true,
    runSideEffects: true,
  };
  const res = await v1Mutate<OrderResponseV1>(`/orders/${id}/status`, 'POST', body);
  return toShopOrder(res);
}
